import React from "react";
import FormGenerator from "components/form-generator/FormGenerator";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import {
  setBreadCrumbs,
  setCurrent,
  setNavigationComponents,
} from "features/navigation/navigationSlice";
import Card from "ui/Card";
import { MinusIcon, PlusIcon, XIcon } from "@heroicons/react/outline";
import { Popover } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/solid";
import customAxios from "middleware/customAxios";
import PrimaryButton from "ui/buttons/PrimaryButton";
import { useNavigate } from "react-router-dom";
import Checkbox from "components/checkbox/Checkbox";
import { itemRender } from "functions/ItemRender";
import DatePickerMain from "components/date-picker-v2/DatePickerMain";
import moment from "moment"; // <-- added moment
import { MultiSelect } from "react-multi-select-component";

// Helper function to format dates for backend API
const formatDateForBackend = (date) => {
  if (!date) return null;
  return moment(date).toISOString();
};

export default function DiscountAdd() {
  let dispatch = useDispatch();
  let navigate = useNavigate();

  // Start with isApplicableToAllProducts = false to show the multi-select immediately
  let [discountData, setDiscountData] = React.useState(() => {
    try {
      return {
        isApplicableToAllProducts: false,
        startDate: moment().startOf("day").toDate(),
        endDate: moment().endOf("day").toDate(),
      };
    } catch (e) {
      return {
        isApplicableToAllProducts: false,
        startDate: new Date(),
        endDate: new Date(Date.now() + 86400000),
      };
    }
  });

  let data = useSelector((state) => state.data.userData);
  let [products, setProducts] = React.useState([]);
  let [outlets, setOutlets] = React.useState([]);
  let [selectedProducts, setSelectedProducts] = React.useState([]);
  let [users, setUsers] = React.useState([]);
  let [selectedUsers, setSelectedUsers] = React.useState([]);
  // Optional: track which quick range was last selected (for styling)
  let [selectedTimeline, setSelectedTimeline] = React.useState("Today");

  React.useEffect(() => {
    dispatch(
      setCurrent({
        name: "Create Product Discount",
      })
    );
    dispatch(setNavigationComponents(null));
    dispatch(
      setBreadCrumbs([
        {
          name: "Home",
          path: "/",
        },
        {
          name: "Discount",
          path: "/discounts",
        },
        {
          name: "Add Product Discount",
          path: "/discounts/add",
        },
      ])
    );
  });

  // Fetch users once on mount so the Users dropdown always has options
  React.useEffect(() => {
    customAxios
      .get("/admin/users")
      .then((res) => {
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        const options = list
          .filter((u) => u != null && (u.id != null || u.username != null))
          .map((u) => {
            const info = u.userInfo ?? u.dataValues?.userInfo ?? {};
            const firstName = info?.firstName ?? "";
            const lastName = info?.lastName ?? "";
            const name = [firstName, lastName].filter(Boolean).join(" ") || u.username || "";
            const label = info?.email ? `${name} (${u.username})`.trim() : (name || u.username || String(u.id));
            return { label: label.trim() || String(u.id), value: u.id, data: u };
          });
        setUsers(options);
      })
      .catch(() => setUsers([]));
  }, []);

  React.useEffect(() => {
    customAxios
      .get("/admin/product-variants")
      .then((res) => {
        const list = res?.data && Array.isArray(res.data) ? res.data : [];
        const options = list.map((product) => ({
          label: `${product.product?.name ?? ""} - ${product.name ?? ""}`.trim() || String(product.id),
          value: product.id,
          data: product,
        }));
        setProducts(options);
      })
      .catch(() => setProducts([]));
    setOutlets(
      data?.outlets?.filter((outlet) => outlet.name !== "Central Inventory") ?? []
    );
  }, [data]);

  // Date change handlers - only allow current and future dates
  const handleStartDateChange = (date) => {
    const start = moment(date).startOf("day");
    const today = moment().startOf("day");
    const end = discountData.endDate ? moment(discountData.endDate).startOf("day") : null;
    // If new start is after current end, set end to start so range stays valid
    const newEnd =
      end && start.isAfter(end) ? start.toDate() : discountData.endDate;
    setDiscountData({
      ...discountData,
      startDate: date,
      endDate: newEnd,
    });
  };

  const handleEndDateChange = (date) => {
    setDiscountData({ ...discountData, endDate: date });
  };

  // Helper to format dates for display
  const formatDateRange = () => {
    try {
      if (discountData?.startDate && discountData?.endDate) {
        return `${moment(discountData.startDate).format("DD MMM YYYY")} - ${moment(
          discountData.endDate
        ).format("DD MMM YYYY")}`;
      }
    } catch (e) {
      return "Select date range";
    }
    return "Select date range";
  };

  return (
    <div className="absolute top-0 w-full  bg-slate-50  grid grid-cols-1  md:grid-cols-3 gap-3">
      <div className="col-span-2 grid grid-cols-1 gap-3 pb-20">
        <Card
          heading={"Amount of Products"}
          description={
            "Add the amount of products you want to add to the discount"
          }
        >
          <div className="flex flex-col h-fit items-start space-y-6 p-1">
            <div className="flex flex-col space-y-2">
              <label className="text-lg font-medium">Method</label>
              {/* radio buttons */}
              <div className="flex flex-col  space-y-2">
                <div className="flex flex-row items-center space-x-2">
                  <Checkbox
                    onClick={() => {
                      setDiscountData({
                        ...discountData,
                        method: discountData?.method === "code" ? undefined : "code",
                      });
                    }}
                    checked={discountData?.method === "code"}
                  />
                  <label>Discount Code</label>
                </div>
                <div className="flex flex-row items-center space-x-2">
                  <Checkbox
                    onClick={() => {
                      setDiscountData({
                        ...discountData,
                        method: discountData?.method === "auto" ? undefined : "auto",
                      });
                    }}
                    checked={discountData?.method === "auto"}
                  />
                  <label>Automatic Discount</label>
                </div>
              </div>
            </div>
            {discountData?.method === "code" ? (
              <div className="flex w-full flex-col space-y-2">
                <label className="text-sm font-medium">Discount Code</label>
                <div className="flex flex-row space-x-3">
                  <input
                    type="text"
                    className="w-full border uppercase border-slate-300 rounded-md p-2"
                    value={discountData.code ?? ""}
                    onChange={(e) => {
                      setDiscountData({
                        ...discountData,
                        code: e.target.value,
                      });
                    }}
                  />
                  <button
                    onClick={() => {
                      setDiscountData({
                        ...discountData,
                        code: Math.random().toString(36).substring(7),
                      });
                    }}
                    className="bg-slate-200 p-2 rounded-md"
                  >
                    Generate
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col space-y-2">
                <label className="text-sm font-medium">Title</label>
                <div className="flex flex-row space-x-3">
                  <input
                    type="text"
                    className="w-full border uppercase border-slate-300 rounded-md p-2"
                    value={discountData.title ?? ""}
                    onChange={(e) => {
                      setDiscountData({
                        ...discountData,
                        code: null,
                        title: e.target.value,
                      });
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
        <Card
          heading={"Value"}
          description={
            "Add the amount of products you want to add to the discount"
          }
        >
          <div className="flex flex-row space-x-10 w-full">
            <div className="w-1/3 flex flex-row text-sm ">
              <div
                onClick={() => {
                  setDiscountData({
                    ...discountData,
                    type: "percentage",
                  });
                }}
                className="flex flex-col w-full space-y-2 border"
              >
                <label
                  className={`${discountData.type == "percentage"
                    ? "bg-slate-300"
                    : "bg-white"
                    } font-medium px-4 p-2 hover:bg-white `}
                >
                  Percentage
                </label>
              </div>
              <div
                onClick={() => {
                  setDiscountData({
                    ...discountData,
                    type: "fixed",
                  });
                }}
                className="flex flex-col w-full space-y-2 border"
              >
                <label
                  className={`${discountData.type == "fixed" ? "bg-slate-300" : "bg-white"
                    } font-medium px-4 p-2 hover:bg-white  `}
                >
                  Fixed Amount
                </label>
              </div>
            </div>
            <div className="flex w-2/3 flex-col space-y-2">
              {discountData?.type === "percentage" ? (
                <div className="flex flex-row items-center  border uppercase border-slate-300 rounded-md p-2 space-x-3">
                  <input
                    type="number"
                    className="w-full h-full focus:outline-none"
                    value={discountData.percentage}
                    onChange={(e) => {
                      setDiscountData({
                        ...discountData,
                        percentage: e.target.value,
                      });
                    }}
                  />
                  <label className="text-sm font-medium">%</label>
                </div>
              ) : (
                <div className="flex flex-row items-center  border uppercase border-slate-300 rounded-md p-2 space-x-3">
                  <label className="text-sm font-medium">MVR</label>
                  <input
                    type="number"
                    className="w-full h-full focus:outline-none"
                    value={discountData.fixedAmount}
                    onChange={(e) => {
                      setDiscountData({
                        ...discountData,
                        fixedAmount: e.target.value,
                      });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
        <Card
          heading={"Minimum Purchase Requirements"}
          description={
            "Add the amount of products you want to add to the discount"
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Minimum Purchase</label>
              <div className="flex flex-row items-center  border uppercase border-slate-300 rounded-md p-2 space-x-3">
                <label className="text-sm font-medium">MVR</label>
                <input
                  type="number"
                  className="w-full h-full focus:outline-none"
                  value={discountData.minimumPurchase}
                  onChange={(e) => {
                    setDiscountData({
                      ...discountData,
                      minimumPurchase: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Minimum Quantity</label>
              <div className="flex flex-row items-center  border uppercase border-slate-300 rounded-md p-2 space-x-3">
                <input
                  type="number"
                  className="w-full h-full focus:outline-none"
                  value={discountData.minQuantity}
                  onChange={(e) => {
                    setDiscountData({
                      ...discountData,
                      minQuantity: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row items-center space-x-2">
              <Checkbox
                onClick={() => {
                  setDiscountData({
                    ...discountData,
                    allowCombinations: discountData?.allowCombinations
                      ? !discountData.allowCombinations
                      : true,
                  });
                }}
                checked={discountData?.allowCombinations == true}
              />
              <label>Allow Combinations</label>
            </div>
          </div>
        </Card>
        <Card heading={"Products"} description={"Add the products"}>
          <div className="flex flex-row items-center space-x-2">
            <Checkbox
              onClick={() => {
                setDiscountData({
                  ...discountData,
                  isApplicableToAllProducts:
                    discountData?.isApplicableToAllProducts
                      ? !discountData.isApplicableToAllProducts
                      : true,
                });
              }}
              checked={discountData?.isApplicableToAllProducts == true}
            />
            <label>Applicable to all products</label>
          </div>

          {/* Single‑select for specific products - only one product at a time */}
          {discountData?.isApplicableToAllProducts === false && (
            <div className="mt-4">
              <select
                className="w-full border border-slate-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                value={
                  selectedProducts?.length > 0 ? selectedProducts[0].value : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) {
                    setSelectedProducts([]);
                    return;
                  }
                  const opt = products.find((p) => String(p.value) === val);
                  setSelectedProducts(opt ? [opt] : []);
                }}
              >
                <option value="">Select a product...</option>
                {products.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>

              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Users</label>
                <MultiSelect
                  options={users}
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                  labelledBy="Select users"
                  placeholder={users.length === 0 ? "Loading users..." : "Select users..."}
                  overrideStrings={{ selectSomeItems: "Select users...", allItemsAreSelected: "All users selected", selectAll: "Select all", search: "Search users" }}
                  className="multi-select-discount-users"
                />
              </div>
            </div>
          )}

          {/* Date Range Picker - inspired by Reports component */}
          <div className="mt-6">
            <h3 className="text-sm font-medium mb-2">Discount Period</h3>
            <Popover className="relative focus:outline-none font-sans">
              {({ open, close }) => (
                <>
                  <Popover.Button
                    className={`${open ? "" : "text-opacity-70"
                      } group rounded-md inline-flex bg-white p-2 items-center text-slate-500 hover:text-slate-700 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 border border-slate-300 w-full justify-between`}
                  >
                    <span className="text-sm font-medium text-black">
                      {formatDateRange()}
                    </span>
                    <ChevronDownIcon
                      className={`${open ? "" : "text-opacity-70"
                        } ml-2 h-5 w-5 text-slate-500 group-hover:text-slate-700`}
                      aria-hidden="true"
                    />
                  </Popover.Button>

                  <Popover.Panel className="absolute z-10 w-max max-w-xl px-4 top-12 left-0">
                    <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="grid gap-2 bg-white p-5">
                        {/* Quick range buttons - only current and future (no past dates) */}
                        <div className="flex flex-row space-x-3 justify-between items-center flex-wrap gap-2">
                          <button
                            className={`text-sm p-1 px-2 rounded-md cursor-pointer ${selectedTimeline === "Today"
                              ? "bg-yellow-500 text-white hover:bg-yellow-400"
                              : "bg-white hover:bg-slate-200 text-slate-900"
                              }`}
                            onClick={() => {
                              const start = moment().startOf("day").toDate();
                              const end = moment().endOf("day").toDate();
                              setDiscountData({ ...discountData, startDate: start, endDate: end });
                              setSelectedTimeline("Today");
                              close();
                            }}
                          >
                            Today
                          </button>
                          <button
                            className={`text-sm p-1 px-2 rounded-md cursor-pointer ${selectedTimeline === "This Week"
                              ? "bg-yellow-500 text-white hover:bg-yellow-400"
                              : "bg-white hover:bg-slate-200 text-slate-900"
                              }`}
                            onClick={() => {
                              const weekStart = moment().startOf("week");
                              const today = moment().startOf("day");
                              const start = (weekStart.isBefore(today) ? today : weekStart).toDate();
                              const end = moment().endOf("week").toDate();
                              setDiscountData({ ...discountData, startDate: start, endDate: end });
                              setSelectedTimeline("This Week");
                              close();
                            }}
                          >
                            This Week
                          </button>
                          <button
                            className={`text-sm p-1 px-2 rounded-md cursor-pointer ${selectedTimeline === "This Month"
                              ? "bg-yellow-500 text-white hover:bg-yellow-400"
                              : "bg-white hover:bg-slate-200 text-slate-900"
                              }`}
                            onClick={() => {
                              const monthStart = moment().startOf("month");
                              const today = moment().startOf("day");
                              const start = (monthStart.isBefore(today) ? today : monthStart).toDate();
                              const end = moment().endOf("month").toDate();
                              setDiscountData({ ...discountData, startDate: start, endDate: end });
                              setSelectedTimeline("This Month");
                              close();
                            }}
                          >
                            This Month
                          </button>
                        </div>

                        {/* Custom date pickers - minDate prevents past dates */}
                        <div className="flex flex-row space-x-3 items-center">
                          <DatePickerMain
                            selected={discountData.startDate}
                            onDateChange={handleStartDateChange}
                            dateFormat="dd/MM/yyyy"
                            className="w-32"
                            minDate={moment().startOf("day").toDate()}
                          />
                          <p className="text-sm font-medium text-slate-900">To</p>
                          <DatePickerMain
                            selected={discountData.endDate}
                            onDateChange={handleEndDateChange}
                            dateFormat="dd/MM/yyyy"
                            className="w-32"
                            minDate={
                              (() => {
                                const today = moment().startOf("day");
                                const start = discountData.startDate
                                  ? moment(discountData.startDate).startOf("day")
                                  : today;
                                return (start.isAfter(today) ? start : today).toDate();
                              })()
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>
          </div>
        </Card>
      </div>
      <div className="col-span-1 relative w-full">
        <div className="bg-white w-[400px] fixed max-h-[calc(100vh-1.5rem)] overflow-y-auto space-y-3 p-5 border border-slate-300 rounded-xl">
          <h1 className="text-xl font-medium flex-shrink-0">Summary</h1>
          <div className="flex flex-row space-x-3 text-xl uppercase">
            {discountData?.code
              ? discountData?.code
              : discountData?.title
                ? discountData?.title
                : ""}
          </div>
          {discountData.type && (
            <>
              {itemRender(
                "Method",
                discountData?.method ? discountData?.method : "Method"
              )}
              {discountData?.type === "percentage" ? (
                <div>
                  {itemRender(
                    "percentage",
                    `${discountData?.percentage} % OFF`
                  )}
                </div>
              ) : (
                <div>
                  {itemRender("Amount", `${discountData?.fixedAmount} MVR OFF`)}
                </div>
              )}
            </>
          )}

          {itemRender(
            "Min. Purchase Amount",
            `${discountData?.minimumPurchase
              ? `${discountData?.minimumPurchase} MVR`
              : "No Minimum Purchase"
            }`
          )}
          {itemRender(
            "Min. Quantity",
            `${discountData?.minQuantity
              ? discountData?.minQuantity
              : "No Minimum Quantity"
            }`
          )}
          {itemRender(
            "Allow Combinations",
            `${discountData?.allowCombinations}`
          )}

          {/* Show selected dates in summary */}
          {discountData.startDate && (
            <div className="space-y-1">
              <h1 className="text-sm text-slate-700 font-medium">Start Date</h1>
              <div>{moment(discountData.startDate).format("DD MMM YYYY")}</div>
            </div>
          )}
          {discountData.endDate && (
            <div className="space-y-1">
              <h1 className="text-sm text-slate-700 font-medium">End Date</h1>
              <div>{moment(discountData.endDate).format("DD MMM YYYY")}</div>
            </div>
          )}

          <div className="space-y-2">
            <h1 className="text-sm text-slate-700 font-medium">
              Products in this discount
            </h1>
            <div className="text-sm text-slate-900">
              {discountData.isApplicableToAllProducts ? (
                "All Products"
              ) : selectedProducts && selectedProducts.length > 0 ? (
                selectedProducts[0].label ||
                (selectedProducts[0].data?.product?.name != null || selectedProducts[0].data?.name != null
                  ? `${selectedProducts[0].data?.product?.name ?? ""} - ${selectedProducts[0].data?.name ?? ""}`.trim()
                  : String(selectedProducts[0].value ?? ""))
              ) : (
                "No products selected"
              )}
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-sm text-slate-700 font-medium">
              Users in this discount
            </h1>
            <div className="text-sm text-slate-900">
              {selectedUsers && selectedUsers.length > 0
                ? selectedUsers.map((u) => u.label).join(", ")
                : "No users selected"}
            </div>
          </div>

          {/* Discount duration: after end date prices revert to original */}
          {discountData.startDate && discountData.endDate && (
            <div className="space-y-1">
              <h1 className="text-sm text-slate-700 font-medium"> Discount Duration</h1>
              <div className="text-sm text-slate-600">
                {moment(discountData.endDate).diff(
                  moment(discountData.startDate),
                  "days"
                )}{" "}
                day(s)
              </div>
            </div>
          )}

          <div className="flex w-full space-x-3 pt-6">
            <button
              onClick={() => {
                // Resolve method: use checkbox if selected; else if title has value use "auto"
                const hasCode = discountData?.method === "code" && discountData?.code?.toString().trim();
                const hasTitle = discountData?.title?.toString().trim();
                const method = discountData?.method === "code"
                  ? "code"
                  : (discountData?.method === "auto" || hasTitle)
                    ? "auto"
                    : hasCode
                      ? "code"
                      : null;
                const name = (method === "code" ? discountData?.code : discountData?.title)?.toString().trim();

                if (!method || !name) {
                  alert("Please enter a Discount Code (and select Discount Code) or a Title to create a discount.");
                  return;
                }

                let data = {
                  name,
                  method,
                  percentage: discountData?.percentage
                    ? parseFloat(discountData.percentage)
                    : null,
                  fixedAmount: discountData?.fixedAmount
                    ? parseFloat(discountData.fixedAmount)
                    : null,
                  minPurchaseAmount: discountData?.minimumPurchase
                    ? parseFloat(discountData.minimumPurchase)
                    : null,
                  minQuantity: discountData?.minQuantity
                    ? parseInt(discountData.minQuantity)
                    : null,
                  allowCombinations: discountData?.allowCombinations || false,
                  isApplicableToAllProducts:
                    selectedProducts?.length > 0
                      ? false
                      : (discountData?.isApplicableToAllProducts || false),
                  startDate: formatDateForBackend(
                    discountData?.startDate || new Date()
                  ),
                  endDate: formatDateForBackend(
                    discountData?.endDate ||
                    new Date(Date.now() + 86400000)
                  ),
                  productVariantIds:
                    selectedProducts?.length > 0
                      ? selectedProducts.map((p) => p.value)
                      : [],
                  userIds:
                    selectedUsers?.length > 0
                      ? selectedUsers.map((u) => u.value)
                      : [],
                };
                customAxios
                  .post("/admin/discount", data)
                  .then((res) => {
                    navigate("/discounts");
                  })
                  .catch((error) => {
                    console.error("Error creating discount:", error);
                    const msg =
                      error.response?.data?.message ||
                      error.message ||
                      "Failed to create discount. Please try again.";
                    alert(msg);
                  });
              }}
              className="bg-slate-800 w-full text-white hover:ring-1 ring-offset-1 font-medium  p-2 rounded-md"
            >
              Submit
            </button>
            <button
              onClick={() => {
                setDiscountData({
                  ...discountData,
                  isApplicableToAllProducts: true,
                });
              }}
              className="bg-white text-slate-800 w-full hover:ring-1 ring-offset-1   font-medium  p-2 rounded-md"
            >
              Discard Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}